// /opt/roadoptix/frontend/src/pages/BrokerLoadDetail.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { apiGetBroker, apiPostBroker, getApiBase } from "../api";

const MAPBOX_TOKEN = "pk.eyJ1Ijoicm9hZG9wdGl4IiwiYSI6ImNtazV0d2MxcTBuN3Eza29sbXJpaGJ3Z2EifQ.hX96aHeeThw3QT7aPGDWZQ";

// Load lifecycle status configuration
const STATUS_CONFIG = {
  DRAFT:       { label: "Draft",        icon: "📝", color: "#6b7280", bg: "#f3f4f6", next: null },
  CREATED:     { label: "Created",      icon: "📝", color: "#6b7280", bg: "#f3f4f6", next: null },
  POSTED:      { label: "Posted",       icon: "📢", color: "#2563eb", bg: "#dbeafe", next: null },
  TENDERED:    { label: "Awarded",      icon: "✅", color: "#059669", bg: "#d1fae5", next: "ACCEPTED" },
  ACCEPTED:    { label: "Accepted",     icon: "🤝", color: "#7c3aed", bg: "#ede9fe", next: "PICKED_UP" },
  PICKED_UP:   { label: "Picked Up",    icon: "🚛", color: "#ea580c", bg: "#fff7ed", next: "IN_TRANSIT" },
  IN_TRANSIT:  { label: "In Transit",   icon: "🛣️", color: "#0891b2", bg: "#cffafe", next: "DELIVERED" },
  IN_PROGRESS: { label: "In Transit",   icon: "🛣️", color: "#0891b2", bg: "#cffafe", next: "DELIVERED" },
  DELIVERED:   { label: "Delivered",    icon: "📦", color: "#16a34a", bg: "#dcfce7", next: "CLOSED" },
  COMPLETED:   { label: "Completed",   icon: "✔️", color: "#065f46", bg: "#d1fae5", next: null },
  CLOSED:      { label: "Closed",      icon: "✔️", color: "#065f46", bg: "#d1fae5", next: null },
  CANCELLED:   { label: "Cancelled",   icon: "❌", color: "#dc2626", bg: "#fee2e2", next: null },
};

const LIFECYCLE_ORDER = ["POSTED", "TENDERED", "ACCEPTED", "PICKED_UP", "IN_TRANSIT", "DELIVERED", "CLOSED"];

function getStatusConfig(status) {
  return STATUS_CONFIG[status] || { label: status, icon: "❓", color: "#6b7280", bg: "#f3f4f6", next: null };
}

function StatusProgressBar({ currentStatus }) {
  const currentIdx = LIFECYCLE_ORDER.indexOf(currentStatus);
  // Map IN_PROGRESS to IN_TRANSIT for display
  const displayIdx = currentStatus === "IN_PROGRESS" ? LIFECYCLE_ORDER.indexOf("IN_TRANSIT") : currentIdx;
  
  if (displayIdx < 0) return null; // DRAFT, CREATED, CANCELLED - no progress bar
  
  return (
    <div style={{ display: "flex", gap: 0, alignItems: "center", margin: "12px 0" }}>
      {LIFECYCLE_ORDER.map((s, i) => {
        const cfg = getStatusConfig(s);
        const isActive = i <= displayIdx;
        const isCurrent = i === displayIdx;
        return (
          <React.Fragment key={s}>
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center", flex: 1,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: isActive ? cfg.color : "#e5e7eb",
                color: "white", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 700,
                border: isCurrent ? `3px solid ${cfg.color}` : "none",
                boxShadow: isCurrent ? `0 0 0 3px ${cfg.bg}` : "none",
              }}>
                {isActive ? "✓" : i + 1}
              </div>
              <div style={{
                fontSize: 10, fontWeight: isCurrent ? 800 : 500, marginTop: 4,
                color: isActive ? cfg.color : "#9ca3af",
              }}>
                {cfg.label}
              </div>
            </div>
            {i < LIFECYCLE_ORDER.length - 1 && (
              <div style={{
                flex: 1, height: 3, maxWidth: 40,
                background: i < displayIdx ? getStatusConfig(LIFECYCLE_ORDER[i+1]).color : "#e5e7eb",
                borderRadius: 2, marginBottom: 18,
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function RouteMap({ originLat, originLng, destLat, destLng, originAddress, destAddress, distanceMiles }) {
  const [routeGeometry, setRouteGeometry] = React.useState(null);
  const [tollInfo, setTollInfo] = React.useState(null);
  const mapContainer = React.useRef(null);
  const map = React.useRef(null);

  React.useEffect(() => {
    // Fetch route from Mapbox Directions API
    async function fetchRoute() {
      try {
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${originLng},${originLat};${destLng},${destLat}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;
        const resp = await fetch(url);
        const data = await resp.json();
        if (data.routes && data.routes[0]) {
          setRouteGeometry(data.routes[0].geometry);
          // Check for tolls (Mapbox doesnt directly provide toll info, but we can estimate based on route)
          const duration = Math.round(data.routes[0].duration / 60);
          setTollInfo({ duration, hasInterstates: true });
        }
      } catch (e) {
        console.error("Failed to fetch route:", e);
      }
    }
    if (originLat && destLat) fetchRoute();
  }, [originLat, originLng, destLat, destLng]);

  React.useEffect(() => {
    if (!mapContainer.current || map.current) return;
    if (!window.mapboxgl) {
      // Load Mapbox GL JS
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css";
      document.head.appendChild(link);
      const script = document.createElement("script");
      script.src = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js";
      script.onload = () => initMap();
      document.head.appendChild(script);
    } else {
      initMap();
    }
    function initMap() {
      window.mapboxgl.accessToken = MAPBOX_TOKEN;
      map.current = new window.mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [(originLng + destLng) / 2, (originLat + destLat) / 2],
        zoom: 5
      });
      map.current.addControl(new window.mapboxgl.NavigationControl(), "top-right");
      // Add markers
      new window.mapboxgl.Marker({ color: "#22c55e" })
        .setLngLat([originLng, originLat])
        .setPopup(new window.mapboxgl.Popup().setHTML(`<b>Origin</b><br/>${originAddress || ""}`))
        .addTo(map.current);
      new window.mapboxgl.Marker({ color: "#ef4444" })
        .setLngLat([destLng, destLat])
        .setPopup(new window.mapboxgl.Popup().setHTML(`<b>Destination</b><br/>${destAddress || ""}`))
        .addTo(map.current);
      // Fit bounds
      const bounds = new window.mapboxgl.LngLatBounds();
      bounds.extend([originLng, originLat]);
      bounds.extend([destLng, destLat]);
      map.current.fitBounds(bounds, { padding: 60 });
    }
  }, [originLat, originLng, destLat, destLng, originAddress, destAddress]);

  React.useEffect(() => {
    if (!map.current || !routeGeometry) return;
    // Wait for map to load
    if (!map.current.loaded()) {
      map.current.on("load", addRoute);
    } else {
      addRoute();
    }
    function addRoute() {
      if (map.current.getSource("route")) {
        map.current.getSource("route").setData(routeGeometry);
      } else {
        map.current.addSource("route", { type: "geojson", data: { type: "Feature", geometry: routeGeometry } });
        map.current.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "#3b82f6", "line-width": 5, "line-opacity": 0.8 }
        });
      }
    }
  }, [routeGeometry]);

  return (
    <div>
      <div ref={mapContainer} style={{ height: 400, borderRadius: 8, overflow: "hidden" }} />
      <div style={{ marginTop: 12, display: "flex", gap: 20, flexWrap: "wrap", fontSize: 14 }}>
        <div><b>📍 Origin:</b> {originAddress}</div>
        <div><b>🏁 Destination:</b> {destAddress}</div>
        <div><b>📏 Distance:</b> {distanceMiles ? `${distanceMiles} mi` : "—"}</div>
        {tollInfo && <div><b>⏱️ Est. Drive Time:</b> {Math.floor(tollInfo.duration / 60)}h {tollInfo.duration % 60}m</div>}
      </div>
    </div>
  );
}


function money(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}

function daysAgo(dt) {
  if (!dt) return null;
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function section(title, children) {
  return (
    <div style={{ marginTop: 16, padding: 16, border: "1px solid #e5e5e5", borderRadius: 12, background: "white" }}>
      <div style={{ fontWeight: 900, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

function pill(txt) {
  const t = String(txt || "").toUpperCase();
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 999,
    fontSize: 12,
    border: "1px solid #ddd",
    background: "#f6f6f6",
  };
  if (t === "SUBMITTED") return { ...base, background: "#fff7db", borderColor: "#ffd56a" };
  if (t === "AWARDED") return { ...base, background: "#eaffea", borderColor: "#9be79b" };
  if (t === "REJECTED") return { ...base, background: "#ffecec", borderColor: "#ffb3b3" };
  return base;
}

function badgeStyle(kind) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    border: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  };

  if (kind === "GOOD") return { ...base, background: "#ecfdf5", color: "#065f46", borderColor: "#a7f3d0" };
  if (kind === "BAD") return { ...base, background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" };
  if (kind === "WARN") return { ...base, background: "#fffbeb", color: "#92400e", borderColor: "#fde68a" };
  return { ...base, background: "#f3f4f6", color: "#374151", borderColor: "#e5e7eb" };
}

function ComplianceBadges({ carrierProfile }) {
  const overall = String(carrierProfile?.fmcsaOverall || "UNKNOWN").toUpperCase();
  const dot = String(carrierProfile?.fmcsaDotStatus || "UNKNOWN").toUpperCase();
  const ins = String(carrierProfile?.fmcsaInsuranceStatus || "UNKNOWN").toUpperCase();
  const mcs = String(carrierProfile?.fmcsaMcs150Status || "UNKNOWN").toUpperCase();

  const overallKind = overall === "OK" ? "GOOD" : overall === "NOT_OK" ? "BAD" : "NEUTRAL";
  const dotKind = dot === "VALID" ? "GOOD" : dot === "INVALID" ? "BAD" : "NEUTRAL";
  const insKind = ins === "VALID" ? "GOOD" : ins === "NEEDS_REVIEW" ? "WARN" : "NEUTRAL";
  const mcsKind = mcs === "CURRENT" ? "GOOD" : mcs === "OUTDATED" ? "WARN" : "NEUTRAL";

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <span style={badgeStyle(overallKind)}>Compliance: {overall}</span>
      <span style={badgeStyle(dotKind)}>DOT License: {dot}</span>
      <span style={badgeStyle(insKind)}>Insurance: {ins}</span>
      <span style={badgeStyle(mcsKind)}>MCS-150: {mcs}</span>
    </div>
  );
}

export default function BrokerLoadDetail() {
  const nav = useNavigate();
  const loc = useLocation();
  const { id } = useParams();
  const apiBase = useMemo(() => getApiBase(), []);

  const [adminEmail, setAdminEmail] = useState(loc.state?.adminEmail || "brokeradmin@roadoptix.com");

  const [load, setLoad] = useState(null);
  const [bids, setBids] = useState([]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [actionMsg, setActionMsg] = useState("");
  const [actionErr, setActionErr] = useState("");
  const [documents, setDocuments] = useState([]);
  const [closing, setClosing] = useState(false);
  const [workingBidId, setWorkingBidId] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  function startEdit() {
    if (!load) return;
    setEditForm({
      originCity: load.originCity || '',
      originState: load.originState || '',
      destCity: load.destCity || '',
      destState: load.destState || '',
      commodity: load.commodity || '',
      weightLbs: load.weightLbs || '',
      pieces: load.pieces || '',
      customerRateCents: load.rateAmountCents || '',
      carrierCostCents: load.carrierCostCents || '',
      distanceMiles: load.distanceMiles || '',
      equipment: (load.modes && load.modes[0]) || 'FLATBED',
      pickupDate: load.pickupDate || '',
      deliveryDate: load.deliveryDate || '',
    });
    setEditMode(true);
    setSaveMsg('');
  }

  async function saveEdit() {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(`${apiBase}/broker/loads/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      setSaveMsg('✅ Saved!');
      setEditMode(false);
      refreshAll();
    } catch (err) {
      setSaveMsg('❌ ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function refreshAll() {
    setErr("");
    setActionMsg("");
    setActionErr("");
    setLoading(true);
    try {
      const detail = await apiGetBroker(`/broker/loads/${id}`, { adminEmail });
      setLoad(detail.load || null);

      const bidResp = await apiGetBroker(`/broker/loads/${id}/bids`, { adminEmail });
      setBids(bidResp.bids || []);
      // Fetch documents
      try {
        const docResp = await fetch(`${apiBase}/broker/loads/${id}/documents`, {
          headers: { "x-admin-email": adminEmail }
        });
        if (docResp.ok) {
          const docData = await docResp.json();
          setDocuments(docData.documents || []);
        }
      } catch (docErr) {
        console.error("Failed to fetch documents", docErr);
      }
    } catch (e) {
      setErr(e.message || String(e));
      setLoad(null);
      setBids([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function awardBid(bidId) {
    if (!bidId) return;
    if (!window.confirm("Award this bid? This will create a tender and mark other bids rejected.")) return;

    setActionMsg("");
    setActionErr("");
    setWorkingBidId(bidId);

    try {
      await apiPostBroker(`/broker/bids/${bidId}/award`, {}, { adminEmail });
      setActionMsg("Bid awarded. Tender created and load moved to TENDERED.");
      await refreshAll();
    } catch (e) {
      setActionErr(e.message || String(e));
    } finally {
      setWorkingBidId(null);
    }
  }
  async function togglePosting() {
    if (!load) return;
    const newPosted = !load.isPosted;
    const action = newPosted ? "post" : "unpost";
    if (!window.confirm(`Are you sure you want to ${action} this load?`)) return;
    setActionMsg("");
    setActionErr("");
    try {
      const res = await fetch(`${apiBase}/broker/loads/${id}/posting`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "x-admin-email": adminEmail },
        body: JSON.stringify({ isPosted: newPosted })
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed");
      setActionMsg(newPosted ? "Load posted to board!" : "Load removed from board.");
      await refreshAll();
      // Auto-trigger backhaul SMS check when a load is posted
      if (newPosted) {
        try {
          const smsRes = await fetch(`${apiBase}/loads/check-backhaul-sms`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "x-admin-email": adminEmail },
            body: JSON.stringify({ loadId: id })
          });
          const smsData = await smsRes.json();
          if (smsData.sent > 0) {
            setActionMsg(`Load posted! 📱 ${smsData.sent} carrier(s) notified via SMS about backhaul match.`);
          }
        } catch (_) {}
      }
    } catch (e) {
      setActionErr(e.message || String(e));
  }
    }
  async function closeLoad() {
    if (!load) return;
    if (!window.confirm("Close this load? This will mark it as complete and enable payment processing.")) return;
    setClosing(true);
    setActionMsg("");
    setActionErr("");
    try {
      const res = await fetch(apiBase + "/broker/loads/" + id + "/status", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-email": adminEmail },
        body: JSON.stringify({ status: "CLOSED", note: "Load closed by broker after POD approval" })
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to close load");
      setActionMsg("Load closed successfully! Carrier can now request Quick Pay.");
      await refreshAll();
    } catch (e) {
      setActionErr(e.message || String(e));
    } finally {
      setClosing(false);
    }
  }

  const canAwardOnThisLoad =
    load &&
    load.isPosted === true &&
    String(load.status) === "POSTED" &&
    !load.carrierCompanyId &&
    String(load.status) !== "CLOSED" &&
    String(load.status) !== "CANCELLED";

  const carrierProfile = load?.carrier?.carrierProfile || null;
  const checkedAt = carrierProfile?.fmcsaCheckedAt || null;
  const ago = daysAgo(checkedAt);
  const snapshotPath = carrierProfile?.fmcsaSnapshotPath || null;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <PageHeader
        title="Broker Load Detail"
        subtitle={load ? `${load.referenceNumber || load.id} • ${load.shipper?.name || "—"}` : `Load ${id}`}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => nav("/app/broker/loads")} style={{ padding: "8px 12px" }}>
          ← Back to Loads
        </button>

        <div style={{ marginLeft: 6 }}>
          <div style={{ fontSize: 13, color: "#666" }}>Admin Email</div>
          <input value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} style={{ width: 340, padding: 8 }} />
        </div>

        <button onClick={refreshAll} style={{ padding: "10px 14px", fontWeight: 900 }} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>

        <div style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>API: {apiBase}</div>
      </div>

      {err ? (
        <div style={{ marginTop: 14, padding: 12, background: "#ffecec", border: "1px solid #ffb3b3", borderRadius: 10 }}>
          {err}
        </div>
      ) : null}

      {actionMsg ? (
        <div style={{ marginTop: 14, padding: 12, background: "#eaffea", border: "1px solid #9be79b", borderRadius: 10 }}>
          {actionMsg}
        </div>
      ) : null}

      {actionErr ? (
        <div style={{ marginTop: 14, padding: 12, background: "#ffecec", border: "1px solid #ffb3b3", borderRadius: 10 }}>
          {actionErr}
        </div>
      ) : null}

      {section(
        "Overview",
        loading ? (
          <div>Loading…</div>
        ) : !load ? (
          <div style={{ color: "#666" }}>No load found.</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ fontWeight: 900, fontSize: 18 }}>{load.referenceNumber || load.id}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  padding: "4px 12px", borderRadius: 999, fontSize: 13, fontWeight: 800,
                  background: getStatusConfig(load.status).bg,
                  color: getStatusConfig(load.status).color,
                  border: `1px solid ${getStatusConfig(load.status).color}22`,
                }}>
                  {getStatusConfig(load.status).icon} {getStatusConfig(load.status).label}
                </span>
                {load.isPosted && <span style={{ fontSize: 12, color: "#2563eb" }}>• Posted to Board</span>}
              </div>
              {(load.status === "DRAFT" || load.status === "CREATED" || load.status === "POSTED") && (
                <button
                  onClick={togglePosting}
                  style={{
                    padding: "6px 14px",
                    background: load.isPosted ? "#ef4444" : "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    fontWeight: 600,
                    cursor: "pointer"
                  }}
                >
                  {load.isPosted ? "📤 Unpost" : "📢 Post to Board"}
              </button>
              )}
              {!["CLOSED", "CANCELLED", "DELIVERED"].includes(load.status) && (
                <button
                  onClick={async () => {
                    if (!window.confirm("Are you sure you want to cancel this load? This action cannot be undone.")) return;
                    try {
                      const res = await fetch(apiBase + "/broker/loads/" + id + "/status", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "x-admin-email": adminEmail },
                        body: JSON.stringify({ status: "CANCELLED", note: "Load cancelled by broker" })
                      });
                      if (!res.ok) throw new Error("Failed to cancel");
                      refreshAll();
                    } catch (e) {
                      alert("Error: " + e.message);
                    }
                  }}
                  style={{
                    padding: "6px 14px",
                    background: "#dc2626",
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    fontWeight: 600,
                    cursor: "pointer",
                    marginLeft: 8
                  }}
                >
                  ❌ Cancel Load
                </button>
              )}

              {/* Next Status Button */}
              {getStatusConfig(load.status).next && (
                <button
                  onClick={async () => {
                    const nextStatus = getStatusConfig(load.status).next;
                    const nextLabel = getStatusConfig(nextStatus).label;
                    if (!window.confirm(`Move load to "${nextLabel}"?`)) return;
                    try {
                      const res = await fetch(apiBase + "/broker/loads/" + id + "/status", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "x-admin-email": adminEmail },
                        body: JSON.stringify({ status: nextStatus, note: `Status changed to ${nextLabel} by broker` })
                      });
                      if (!res.ok) throw new Error("Failed to update status");
                      refreshAll();
                    } catch (e) {
                      alert("Error: " + e.message);
                    }
                  }}
                  style={{
                    padding: "6px 14px",
                    background: getStatusConfig(getStatusConfig(load.status).next).color,
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    fontWeight: 600,
                    cursor: "pointer",
                    marginLeft: 8
                  }}
                >
                  {getStatusConfig(getStatusConfig(load.status).next).icon} Move to {getStatusConfig(getStatusConfig(load.status).next).label}
                </button>
              )}

              <div style={{ marginLeft: "auto", color: "#666" }}>
                Pickup <b>{fmt(load.pickupStart)}</b>
              </div>
            </div>
            
            {/* Status Progress Bar */}
            <StatusProgressBar currentStatus={load.status} />

            <div>
              <b>From:</b> {load.originAddress || "—"} <span style={{ color: "#aaa" }}>→</span>{" "}
              <b>To:</b> {load.destAddress || "—"}
            </div>

            <div style={{ color: "#555" }}>
              <b>Shipper:</b> {load.shipper?.name || "—"} &nbsp;&nbsp; <b>Equipment:</b> {load.equipmentType || "—"}{" "}
              &nbsp;&nbsp; <b>Rate:</b> {money(load.rateAmountCents)} &nbsp;&nbsp;{" "}
              <b>Distance:</b> {load.distanceMiles ?? "—"} mi
            </div>

            {/* ✅ Carrier Compliance Status */}
            <div style={{ marginTop: 6, paddingTop: 10, borderTop: "1px solid #eee" }}>
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Carrier Compliance</div>

              {!load.carrier ? (
                <div style={{ color: "#666" }}>
                  No carrier assigned yet. (Compliance will show once a carrier is tendered/assigned.)
                </div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ color: "#555" }}>
                    <b>Carrier:</b> {load.carrier?.name || "—"}{" "}
                    {carrierProfile?.dotNumber ? (
                      <>
                        &nbsp;&nbsp; <b>DOT:</b> {carrierProfile.dotNumber}
                      </>
                    ) : null}
                  </div>

                  <ComplianceBadges carrierProfile={carrierProfile} />

                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    <b>Last checked:</b>{" "}
                    {checkedAt ? (
                      <>
                        {fmt(checkedAt)} {ago !== null ? <>({ago === 0 ? "today" : `${ago}d ago`})</> : null}
                      </>
                    ) : (
                      "—"
                    )}
                  </div>

                  {snapshotPath ? (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      <b>Snapshot:</b> <code style={{ fontSize: 11 }}>{snapshotPath}</code>
                      <div style={{ marginTop: 4, color: "#9ca3af" }}>
                        (If you add a backend download route later, we can make this clickable.)
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )
      )}
      {/* Documents & Load Actions */}
      {/* Route Map */}
      {load && load.originLat && load.destLat && section(
        "🗺️ Route Map",
        <RouteMap
          originLat={load.originLat}
          originLng={load.originLng}
          destLat={load.destLat}
          destLng={load.destLng}
          originAddress={load.originAddress}
          destAddress={load.destAddress}
          distanceMiles={load.distanceMiles}
        />
      )}

      {load && ["DELIVERED", "CLOSED"].includes(load.status) && section(
        "📄 Documents & Completion",
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 16 }}>Load Status:</span>
            <span style={{
              padding: "6px 16px",
              borderRadius: 20,
              fontWeight: 700,
              fontSize: 14,
              background: load.status === "CLOSED" ? "#d1fae5" : "#dbeafe",
              color: load.status === "CLOSED" ? "#065f46" : "#1e40af"
            }}>
              {load.status}
            </span>
          </div>
          {documents.length > 0 ? (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Uploaded Documents:</div>
              {documents.map(doc => (
                <div key={doc.id} style={{
                  padding: 12,
                  background: doc.reviewStatus === "APPROVED" ? "#f0fdf4" : doc.reviewStatus === "REJECTED" ? "#fef2f2" : "#f9fafb",
                  border: doc.reviewStatus === "APPROVED" ? "1px solid #86efac" : doc.reviewStatus === "REJECTED" ? "1px solid #fecaca" : "1px solid #e5e7eb",
                  borderRadius: 8,
                  marginBottom: 8,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>📄 {doc.type}</span> - {doc.fileName}
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                      Uploaded: {new Date(doc.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <span style={{
                    padding: "4px 12px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    background: doc.reviewStatus === "APPROVED" ? "#d1fae5" : doc.reviewStatus === "REJECTED" ? "#fee2e2" : "#fef3c7",
                    color: doc.reviewStatus === "APPROVED" ? "#065f46" : doc.reviewStatus === "REJECTED" ? "#991b1b" : "#92400e"
                  }}>
                    {doc.reviewStatus || "PENDING"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#6b7280" }}>No documents uploaded yet.</div>
          )}
          {load.status === "DELIVERED" && (
            <div style={{ padding: 16, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8 }}>
              {documents.some(d => d.type === "POD" && d.reviewStatus === "APPROVED") ? (
                <div>
                  <div style={{ fontWeight: 600, color: "#065f46", marginBottom: 12 }}>
                    ✅ POD Approved - Ready to Close
                  </div>
                  <button
                    onClick={closeLoad}
                    disabled={closing}
                    style={{
                      padding: "12px 24px",
                      background: "#059669",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 16,
                      cursor: closing ? "not-allowed" : "pointer"
                    }}
                  >
                    {closing ? "Closing..." : "✅ Close Load & Enable Payment"}
                  </button>
                </div>
              ) : (
                <div style={{ color: "#92400e" }}>
                  <strong>⏳ Awaiting POD Approval</strong>
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    Review and approve the Proof of Delivery in "Pending Documents" before closing this load.
                  </div>
                </div>
              )}
            </div>
          )}
          {load.status === "CLOSED" && (
            <div style={{ padding: 16, background: "#d1fae5", border: "1px solid #86efac", borderRadius: 8 }}>
              <div style={{ fontWeight: 700, color: "#065f46" }}>
                ✅ Load Closed
              </div>
              <div style={{ fontSize: 13, color: "#065f46", marginTop: 4 }}>
                This load is complete. If Quick Pay was enabled, the carrier can now request payment.
              </div>
            </div>
          )}
        </div>
      )}

      {section(
        `Bids (${bids.length})`,
        loading ? (
          <div>Loading…</div>
        ) : !bids.length ? (
          <div style={{ color: "#666" }}>No bids yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {!canAwardOnThisLoad ? (
              <div style={{ fontSize: 12, color: "#666" }}>
                Awarding is disabled unless the load is <b>POSTED</b>, posted to market, and not already assigned/tendered.
              </div>
            ) : null}

            {bids.map((b) => {
              const disabled =
                !canAwardOnThisLoad ||
                String(b.status).toUpperCase() !== "SUBMITTED" ||
                workingBidId === b.id;

              return (
                <div key={b.id} style={{ border: "1px solid #eee", borderRadius: 12, padding: 12 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 900 }}>
                      {b.carrierCompany?.name || b.carrierCompanyId || "Carrier"}
                    </div>
                    <span style={pill(b.status)}>{b.status}</span>
                    <div style={{ color: "#666", fontSize: 13 }}>
                      Bid: <b>{money(b.amountCents)}</b>
                    </div>
                    <div style={{ marginLeft: "auto", color: "#666", fontSize: 12 }}>
                      {fmt(b.createdAt)}
                    </div>
                  </div>

                  {b.message ? (
                    <div style={{ marginTop: 8, color: "#444" }}>
                      <b>Message:</b> {b.message}
                    </div>
                  ) : null}

                  <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      onClick={() => awardBid(b.id)}
                      disabled={disabled}
                      style={{ padding: "10px 14px", fontWeight: 900 }}
                    >
                      {workingBidId === b.id ? "Awarding..." : "Award Bid → Create Tender"}
                    </button>

                    <div style={{ fontSize: 12, color: "#666", alignSelf: "center" }}>
                      Bidder: {b.createdByUser?.email || "—"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {section(
        "Recent Events",
        !load?.events?.length ? (
          <div style={{ color: "#666" }}>No events.</div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {load.events.slice(0, 25).map((e) => (
              <div key={e.id} style={{ border: "1px solid #eee", borderRadius: 10, padding: 10 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 800 }}>{e.type}</div>
                  <div style={{ color: "#666", fontSize: 12 }}>{fmt(e.createdAt)}</div>
                  <div style={{ marginLeft: "auto", color: "#666", fontSize: 12 }}>
                    {e.actorUser?.email ? <>by {e.actorUser.email}</> : null}
                  </div>
                </div>
                <div style={{ marginTop: 6, color: "#444" }}>{e.message}</div>
              </div>
            ))}
          </div>
        )
      )}

      {/* EDIT MODE PANEL */}
      {load && (
        <div style={{ marginTop: 16, background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontWeight: 800 }}>
              {editMode ? '✏️ Editing Load' : '📝 Load Details'}
            </h3>
            <div style={{ display: 'flex', gap: 8 }}>
              {!editMode ? (
                <button onClick={startEdit}
                  style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                  ✏️ Edit Load
                </button>
              ) : (
                <>
                  <button onClick={saveEdit} disabled={saving}
                    style={{ padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                    {saving ? '⏳ Saving...' : '💾 Save'}
                  </button>
                  <button onClick={() => { setEditMode(false); setSaveMsg(''); }}
                    style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                    Cancel
                  </button>
                </>
              )}
              {saveMsg && <span style={{ fontSize: 13, fontWeight: 600, padding: '8px 0' }}>{saveMsg}</span>}
            </div>
          </div>
          
          {editMode && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Origin City</label>
                <input value={editForm.originCity} onChange={e => setEditForm({...editForm, originCity: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Origin State</label>
                <input value={editForm.originState} onChange={e => setEditForm({...editForm, originState: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} maxLength={2} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Dest City</label>
                <input value={editForm.destCity} onChange={e => setEditForm({...editForm, destCity: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Dest State</label>
                <input value={editForm.destState} onChange={e => setEditForm({...editForm, destState: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} maxLength={2} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Commodity</label>
                <input value={editForm.commodity} onChange={e => setEditForm({...editForm, commodity: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Equipment</label>
                <select value={editForm.equipment} onChange={e => setEditForm({...editForm, equipment: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}>
                  {['FLATBED','STEP_DECK','LOWBOY','DRY_VAN','REEFER','TANKER','HOTSHOT','POWER_ONLY'].map(eq =>
                    <option key={eq} value={eq}>{eq.replace(/_/g, ' ')}</option>
                  )}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#059669', display: 'block', marginBottom: 4 }}>💰 Customer Rate ($)</label>
                <input type="number" value={editForm.customerRateCents ? (editForm.customerRateCents / 100).toFixed(0) : ''}
                  onChange={e => setEditForm({...editForm, customerRateCents: Math.round(Number(e.target.value) * 100)})}
                  placeholder="e.g. 3500"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #059669', fontSize: 14, fontWeight: 700, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', display: 'block', marginBottom: 4 }}>🚛 Carrier Cost ($)</label>
                <input type="number" value={editForm.carrierCostCents ? (editForm.carrierCostCents / 100).toFixed(0) : ''}
                  onChange={e => setEditForm({...editForm, carrierCostCents: Math.round(Number(e.target.value) * 100)})}
                  placeholder="e.g. 2800"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #dc2626', fontSize: 14, fontWeight: 700, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                  📊 Margin: ${editForm.customerRateCents && editForm.carrierCostCents
                    ? ((editForm.customerRateCents - editForm.carrierCostCents) / 100).toFixed(0) : '—'}
                </label>
                <input type="number" value={editForm.distanceMiles || ''} onChange={e => setEditForm({...editForm, distanceMiles: e.target.value})}
                  placeholder="Distance (miles)"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Weight (lbs)</label>
                <input type="number" value={editForm.weightLbs || ''} onChange={e => setEditForm({...editForm, weightLbs: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Pickup Date</label>
                <input type="date" value={editForm.pickupDate || ''} onChange={e => setEditForm({...editForm, pickupDate: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Delivery Date</label>
                <input type="date" value={editForm.deliveryDate || ''} onChange={e => setEditForm({...editForm, deliveryDate: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
